import { useCallback, useEffect, useMemo, useState } from "react";
import { useChannelMessages } from "@/hooks/use-channel-messages";
import { useMessageReactions } from "@/hooks/use-message-reactions";
import { useChatSummary } from "@/hooks/use-chat-summary";
import type { ChannelMember } from "@/hooks/use-chat-channels";
import type { UserLookup } from "@/hooks/use-users";
import { firstUnreadIndex, formatMessageTime } from "@/lib/chat-room/messages";
import type { Channel } from "@/lib/chat-room/channels";
import { ChatChannelHeader } from "./ChatChannelHeader";
import { ChatMessageList } from "./ChatMessageList";
import { ChatComposer } from "./ChatComposer";
import { ProjectChannelLink } from "./ProjectChannelLink";
import { ReactionBar } from "./ReactionBar";
import { SystemMessageLink } from "./SystemMessageLink";
import { ChatSummaryButton } from "./ChatSummaryButton";
import { ChatSummaryCard } from "./ChatSummaryCard";

interface ChatConversationProps {
  channel: Channel;
  selfId: string;
  members: ChannelMember[];
  users: UserLookup[];
}

/**
 * Den åpne kanalen: hode, meldinger og skrivefelt. Antall uleste fryses når
 * kanalen åpnes, så «N nye»-streken blir stående selv om kanalen markeres
 * som lest med en gang.
 */
export function ChatConversation({ channel, selfId, members, users }: ChatConversationProps) {
  const data = useChannelMessages(channel, selfId);
  // Siden monterer komponenten på nytt per kanal (key), så startverdien er
  // antallet uleste da kanalen ble åpnet.
  const [unreadAtOpen] = useState(channel.unread);
  const { markRead, messages } = data;
  const lastId = messages[messages.length - 1]?.id;

  // Marker som lest ved åpning og når nye meldinger kommer mens kanalen er åpen.
  useEffect(() => {
    markRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- markRead er ny hver render
  }, [channel.key, lastId]);

  const nameById = useMemo(() => new Map(users.map((u) => [u.id, u.full_name])), [users]);
  const userName = useCallback((id: string) => nameById.get(id) ?? null, [nameById]);
  const dividerIndex = firstUnreadIndex(messages, unreadAtOpen, selfId);
  const reactions = useMessageReactions(channel, messages, selfId);
  const summary = useChatSummary();
  const [summaryOpen, setSummaryOpen] = useState(false);
  // Oppsummeringen tar meldingene fra den første uleste da kanalen ble åpnet.
  const firstUnread = dividerIndex >= 0 ? messages[dividerIndex] : null;
  const toggleSummary = () => {
    if (summaryOpen) {
      setSummaryOpen(false);
      return;
    }
    if (!firstUnread) return;
    setSummaryOpen(true);
    if (!summary.data && !summary.isPending) summary.mutate({ channelKey: channel.key, since: firstUnread.createdAt });
  };

  // @-forslag: i rom bare medlemmene (andre får ikke lest meldingen), i en
  // prosjektkanal alle ansatte, som i Prosjektoppdateringer.
  const mentionCandidates = useMemo(() => {
    const others = users.filter((u) => u.id !== selfId);
    if (channel.kind === "project") return others;
    const memberIds = new Set(members.map((m) => m.user_id));
    return others.filter((u) => memberIds.has(u.id));
  }, [users, members, channel.kind, selfId]);

  return (
    <section aria-label={channel.title} className="flex min-h-0 min-w-0 flex-1 flex-col">
      <ChatChannelHeader
        channel={channel}
        members={members}
        actions={
          <>
            {channel.kind === "project" && <ProjectChannelLink channel={channel} />}
            <ChatSummaryButton unread={firstUnread ? unreadAtOpen : 0} open={summaryOpen} onClick={toggleSummary} />
          </>
        }
      />
      {summaryOpen && firstUnread && (
        <ChatSummaryCard
          unread={unreadAtOpen}
          sinceLabel={formatMessageTime(firstUnread.createdAt)}
          isLoading={summary.isPending}
          error={summary.error?.message ?? null}
          summary={summary.data ?? null}
          onClose={() => setSummaryOpen(false)}
        />
      )}
      <ChatMessageList
        messages={messages}
        selfId={selfId}
        dividerIndex={dividerIndex}
        dividerLabel={`${unreadAtOpen} ${unreadAtOpen === 1 ? "ny" : "nye"}`}
        isLoading={data.isLoading}
        hasMore={data.hasMore}
        isFetchingMore={data.isFetchingMore}
        onLoadMore={data.loadMore}
        userName={userName}
        renderFooter={(m) =>
          m.kind === "user" ? (
            <ReactionBar
              reactions={reactions.summaryFor(m.id)}
              onToggle={(emoji, mine) => reactions.toggle({ messageId: m.id, emoji, mine })}
              userName={userName}
              disabled={reactions.isPending}
            />
          ) : null
        }
        renderLink={(m) => (m.link ? <SystemMessageLink link={m.link} /> : null)}
      />
      <ChatComposer channelName={channel.title} users={mentionCandidates} onSend={data.send} />
    </section>
  );
}
